import React from 'react';
import { Invitation, roleColors } from './types';
import { formatDate, isExpired } from './utils';

interface InvitationsTabProps {
  invitations: Invitation[];
  isAdmin: boolean;
  inviteEmail: string;
  setInviteEmail: (v: string) => void;
  inviteRole: 'admin' | 'member';
  setInviteRole: (v: 'admin' | 'member') => void;
  inviteLoading: boolean;
  resendingId: string | null;
  onInvite: (e: React.FormEvent) => void;
  onResend: (id: string) => void;
  onCancel: (id: string) => void;
}

export const InvitationsTab: React.FC<InvitationsTabProps> = ({
  invitations,
  isAdmin,
  inviteEmail,
  setInviteEmail,
  inviteRole,
  setInviteRole,
  inviteLoading,
  resendingId,
  onInvite,
  onResend,
  onCancel,
}) => {
  const pending = invitations.filter((i) => i.status === 'pending');

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
            Invite Team Member
          </h3>
          <form onSubmit={onInvite} className="flex flex-wrap gap-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Email address"
              className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button
              type="submit"
              disabled={inviteLoading || !inviteEmail}
              className="px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {inviteLoading ? 'Sending...' : 'Send Invitation'}
            </button>
          </form>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Pending Invitations ({pending.length})
          </h3>
        </div>

        {pending.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
            No pending invitations
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {pending.map((invitation) => (
              <div key={invitation.id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {invitation.email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Invited as{' '}
                    <span className={`px-1.5 py-0.5 rounded ${roleColors[invitation.role]}`}>
                      {invitation.role}
                    </span>{' '}
                    by {invitation.invitedBy.firstName || invitation.invitedBy.email}
                  </p>
                  <p
                    className={`text-xs mt-0.5 ${
                      isExpired(invitation.expiresAt)
                        ? 'text-red-500 dark:text-red-400'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {isExpired(invitation.expiresAt)
                      ? 'Expired'
                      : `Expires ${formatDate(invitation.expiresAt)}`}
                  </p>
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onResend(invitation.id)}
                      disabled={resendingId === invitation.id}
                      className="px-3 py-1.5 text-xs bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 rounded-md hover:bg-teal-100 dark:hover:bg-teal-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resendingId === invitation.id ? 'Sending...' : 'Resend'}
                    </button>
                    <button
                      onClick={() => onCancel(invitation.id)}
                      className="px-3 py-1.5 text-xs bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
